<template>
    <v-container fluid class="bracket-page">
        <page-header :title="pageTitle" icon="mdi-tournament">
            <template v-slot:actions>
                <v-btn small text @click="$router.back()">
                    <v-icon left small>mdi-arrow-left</v-icon>Back
                </v-btn>
                <v-btn v-if="bracketId" small text color="red darken-1" @click="confirmDelete = true">
                    <v-icon left small>mdi-delete-outline</v-icon>Delete
                </v-btn>
                <v-btn
                    small
                    depressed
                    color="black"
                    class="white--text"
                    :loading="saving"
                    :disabled="!seasonId || errors.length > 0"
                    @click="save"
                >
                    <v-icon left small>mdi-content-save-outline</v-icon>Save
                </v-btn>
            </template>
            <div class="bracket-page__intro">
                {{ cup
                    ? 'Διάλεξε τις ομάδες (seeds) και ποιοι παίζουν με ποιους στον πρώτο γύρο.'
                    : 'Τα seeds βγαίνουν από τη βαθμολογία της κανονικής περιόδου (ή τα ορίζεις εσύ).' }}
                Οι νικητές δύο διαδοχικών ζευγαριών συναντώνται στον επόμενο γύρο. Το δέντρο συμπληρώνεται μόνο του
                από τους αγώνες {{ cup ? 'κυπέλλου' : 'των playoffs' }} μεταξύ των δύο ομάδων κάθε ζευγαριού.
            </div>
        </page-header>

        <v-alert v-if="message" :type="message.type" dense text dismissible class="mb-4" @input="message = null">
            {{ message.text }}
        </v-alert>

        <v-row>
            <!-- season, seeds -->
            <v-col cols="12" md="4">
                <v-card class="bracket-card" elevation="1">
                    <div class="bracket-card__title">Ρυθμίσεις</div>
                    <v-select
                        v-model="seasonId"
                        :items="seasons"
                        :item-text="seasonLabel"
                        item-value="id"
                        label="Season"
                        dense
                        outlined
                    />
                    <v-text-field
                        v-model="title"
                        :placeholder="defaultTitle"
                        label="Τίτλος (προαιρετικός)"
                        dense
                        outlined
                        persistent-placeholder
                    />
                    <v-radio-group v-if="!cup" v-model="seeding" dense class="mt-0" hide-details>
                        <v-radio value="standings" label="Seeds από τη βαθμολογία (regular season)" />
                        <v-radio value="manual" label="Seeds που ορίζω εγώ" />
                    </v-radio-group>
                    <v-select
                        :value="teamCount"
                        :items="teamCountItems"
                        label="Ομάδες"
                        dense
                        outlined
                        class="mt-4"
                        hint="Αλλάζοντάς το, τα ζευγάρια ξαναστήνονται (1-8, 4-5, 2-7, 3-6...)"
                        persistent-hint
                        @change="changeTeamCount"
                    />
                    <v-btn small text class="mt-2 px-1" @click="resetPairs">
                        <v-icon left small>mdi-restore</v-icon>Προεπιλεγμένα ζευγάρια
                    </v-btn>

                    <!-- the teams of the seeds, by hand -->
                    <div v-if="seeding === 'manual'" class="mt-4">
                        <div class="bracket-card__subtitle">Seeds</div>
                        <div v-for="(teamId, index) in manualTeams" :key="index" class="seed-row">
                            <span class="seed-row__num">#{{ index + 1 }}</span>
                            <v-autocomplete
                                :value="teamId"
                                :items="teamItems"
                                item-text="name"
                                item-value="id"
                                placeholder="Ομάδα"
                                dense
                                outlined
                                hide-details
                                clearable
                                @change="setManualTeam(index, $event)"
                            />
                        </div>
                    </div>

                    <!-- the seeds from the standings -->
                    <div v-else class="mt-4">
                        <div class="bracket-card__subtitle">Βαθμολογία κανονικής περιόδου</div>
                        <div v-if="!ranking.length" class="text-caption grey--text">Δεν υπάρχει βαθμολογία για αυτή τη σεζόν.</div>
                        <div v-for="rank in ranking.slice(0, teamCount)" :key="rank.teamId" class="seed-row seed-row--static">
                            <span class="seed-row__num">#{{ rank.position }}</span>
                            <span class="seed-row__name">{{ rank.name }}</span>
                            <span class="seed-row__record">{{ rank.won }}-{{ rank.lost }}</span>
                        </div>
                    </div>
                </v-card>
            </v-col>

            <!-- the first round -->
            <v-col cols="12" md="5">
                <v-card class="bracket-card" elevation="1">
                    <div class="bracket-card__title">{{ roundsForm[0] ? roundsForm[0].name || 'Πρώτος γύρος' : 'Πρώτος γύρος' }}</div>
                    <div v-for="(group, groupIndex) in pairGroups" :key="groupIndex" :class="['pair-group', { 'pair-group--single': group.length === 1 }]">
                        <div v-for="item in group" :key="item.index" class="pair-row">
                            <span class="pair-row__num">{{ item.index + 1 }}</span>
                            <v-select
                                :value="item.pair.home"
                                :items="seedItems"
                                dense
                                outlined
                                hide-details
                                :error="isWrongSeed(item.pair.home)"
                                @change="setSeed(item.index, 'home', $event)"
                            />
                            <span class="pair-row__vs">vs</span>
                            <v-select
                                :value="item.pair.away"
                                :items="seedItems"
                                dense
                                outlined
                                hide-details
                                :error="isWrongSeed(item.pair.away)"
                                @change="setSeed(item.index, 'away', $event)"
                            />
                            <div class="pair-row__tools">
                                <v-btn icon x-small title="Αλλαγή έδρας" @click="swapPair(item.index)">
                                    <v-icon small>mdi-swap-horizontal</v-icon>
                                </v-btn>
                                <v-btn icon x-small :disabled="item.index === 0" title="Πάνω" @click="movePair(item.index, -1)">
                                    <v-icon small>mdi-arrow-up</v-icon>
                                </v-btn>
                                <v-btn icon x-small :disabled="item.index === pairs.length - 1" title="Κάτω" @click="movePair(item.index, 1)">
                                    <v-icon small>mdi-arrow-down</v-icon>
                                </v-btn>
                            </div>
                        </div>
                        <div v-if="group.length > 1" class="pair-group__next">
                            <v-icon x-small>mdi-arrow-right-bottom</v-icon>
                            οι νικητές συναντώνται: {{ roundsForm[1] ? roundsForm[1].name : '' }}
                        </div>
                    </div>
                    <v-alert v-for="error in errors" :key="error" type="error" dense text class="mt-3 mb-0">{{ error }}</v-alert>
                </v-card>
            </v-col>

            <!-- the format of every round -->
            <v-col cols="12" md="3">
                <v-card class="bracket-card" elevation="1">
                    <div class="bracket-card__title">Γύροι</div>
                    <div v-for="(round, index) in roundsForm" :key="index" class="round-row">
                        <v-text-field v-model="round.name" :label="`Γύρος ${index + 1}`" dense outlined hide-details />
                        <div class="round-row__format">
                            <v-select
                                v-model="round.bestOf"
                                :items="bestOfItems"
                                dense
                                outlined
                                hide-details
                                @change="value => { if (value === 1) round.advantage = false }"
                            />
                            <v-switch
                                v-model="round.advantage"
                                :disabled="round.bestOf === 1"
                                label="Αβαντάζ 1-0"
                                title="Το καλύτερο seed ξεκινά τη σειρά με μία νίκη"
                                dense
                                inset
                                hide-details
                                class="mt-0 ml-2"
                            />
                        </div>
                    </div>
                </v-card>
            </v-col>
        </v-row>

        <!-- the bracket as the site shows it -->
        <v-card class="bracket-card mt-2" elevation="1">
            <div class="bracket-preview__head">
                <div class="bracket-card__title mb-0">
                    Προεπισκόπηση
                    <v-progress-circular v-if="previewLoading" indeterminate size="16" width="2" class="ml-2" />
                </div>
                <v-btn small depressed :disabled="!preview" :loading="exporting" @click="downloadImage">
                    <v-icon left small>mdi-image-outline</v-icon>Λήψη εικόνας
                </v-btn>
            </div>
            <v-alert v-if="previewError" type="warning" dense text class="mb-0">{{ previewError }}</v-alert>
            <bracket-board
                v-if="preview"
                ref="board"
                :bracket="preview"
                :logos="!exporting"
                :link-matches="false"
                :responsive="false"
            />
        </v-card>

        <confirm-dialog
            v-model="confirmDelete"
            title="Διαγραφή δέντρου"
            :text="cup ? 'Το site δεν θα δείχνει δέντρο για το κύπελλο.' : 'Το site θα δείχνει τα ζευγάρια από τη βαθμολογία (οι 8 πρώτοι), χωρίς αποτελέσματα.'"
            confirm-text="Delete"
            :loading="deleting"
            @confirm="remove"
        />
    </v-container>
</template>

<script>
    import { toPng } from 'html-to-image'
    import api from '@/utils/api'
    import bracketService from '@/services/bracket'
    import leagueService from '@/services/league'
    import seasonService from '@/services/season'
    import configService from '@/services/config'
    import teamService from '@/services/team'
    import PageHeader from '@/components/PageHeader'
    import ConfirmDialog from '@/components/ConfirmDialog'
    import BracketBoard from '@/components/BracketBoard'
    import { seasonLabel, sortSeasons } from '@/utils/season'

    // 1, 8, 4, 5, 2, 7, 3, 6: the best seeds meet as late as possible
    function seedOrder(size) {
        let order = [1, 2]
        for (let next = 4; next <= size; next *= 2) {
            order = order.flatMap(seed => [seed, next + 1 - seed])
        }
        return size === 1 ? [1] : order
    }

    function powerOfTwo(count) {
        let size = 2
        while (size < count) size *= 2
        return size
    }

    // remaining: 1 for the final, 2 for the semifinals, ...
    function roundName(remaining, index) {
        return ['Τελικός', 'Ημιτελικά', 'Προημιτελικά', 'Φάση των 16'][remaining - 1] || `Γύρος ${index + 1}`
    }

    export default {
        name: 'Bracket',
        components: { PageHeader, ConfirmDialog, BracketBoard },
        data() {
            return {
                seasons: [],
                seasonId: null,
                scopeTitle: '',
                teams: [],
                ranking: [],
                bracketId: null,
                title: '',
                seeding: 'standings',
                teamCount: 8,
                manualTeams: [],
                pairs: [],
                roundsForm: [],
                preview: null,
                previewError: null,
                previewLoading: false,
                previewTimer: null,
                previewRequest: 0,
                message: null,
                saving: false,
                deleting: false,
                exporting: false,
                confirmDelete: false,
                bestOfItems: [
                    { text: 'Ένας αγώνας', value: 1 },
                    { text: 'Best of 3', value: 3 },
                    { text: 'Best of 5', value: 5 },
                    { text: 'Best of 7', value: 7 }
                ]
            }
        },
        computed: {
            // /brackets/league/:leagueId, /brackets/cup (the cup of the season) or /brackets/cup/:cupLeagueId
            leagueId() {
                return this.$route.params.leagueId ? Number(this.$route.params.leagueId) : null
            },
            cupLeagueId() {
                return this.$route.params.cupLeagueId ? Number(this.$route.params.cupLeagueId) : null
            },
            cup() {
                return !this.leagueId
            },
            pageTitle() {
                return this.cup ? `Δέντρο κυπέλλου${this.scopeTitle ? ` · ${this.scopeTitle}` : ''}` : `Δέντρο playoffs · ${this.scopeTitle}`
            },
            defaultTitle() {
                return this.cup ? (this.scopeTitle || 'Κύπελλο') : `Playoffs ${this.scopeTitle}`
            },
            teamCountItems() {
                return Array.from({ length: 31 }, (item, index) => index + 2)
            },
            teamItems() {
                if (this.leagueId) {
                    const inLeague = this.teams.filter(team =>
                        (team.leagues || []).some(league => league.id === this.leagueId) || (team.league && team.league.id === this.leagueId))
                    if (inLeague.length) return inLeague
                }
                if (this.cupLeagueId) {
                    const inCup = this.teams.filter(team => team.cupLeague && team.cupLeague.id === this.cupLeagueId)
                    if (inCup.length) return inCup
                }
                return this.teams
            },
            seedItems() {
                const items = [{ text: 'Bye', value: 0 }]
                for (let seed = 1; seed <= this.teamCount; seed++) {
                    items.push({ text: `#${seed} ${this.seedName(seed)}`.trim(), value: seed })
                }
                return items
            },
            pairGroups() {
                const groups = []
                this.pairs.forEach((pair, index) => {
                    if (index % 2 === 0) groups.push([])
                    groups[groups.length - 1].push({ pair, index })
                })
                return groups
            },
            usedSeeds() {
                return this.pairs.flatMap(pair => [pair.home, pair.away]).filter(seed => seed > 0)
            },
            errors() {
                const errors = []
                const duplicates = [...new Set(this.usedSeeds.filter((seed, index) => this.usedSeeds.indexOf(seed) !== index))]
                if (duplicates.length) errors.push(`Το seed ${duplicates.join(', ')} μπαίνει δύο φορές.`)
                const missing = []
                for (let seed = 1; seed <= this.teamCount; seed++) {
                    if (!this.usedSeeds.includes(seed)) missing.push(seed)
                }
                if (missing.length) errors.push(`Δεν έχουν μπει τα seeds ${missing.join(', ')}.`)
                if (this.pairs.some(pair => !pair.home && !pair.away)) errors.push('Ένα ζευγάρι δεν μπορεί να έχει δύο Bye.')
                if (this.seeding === 'manual') {
                    const empty = this.manualTeams.map((teamId, index) => (teamId ? null : index + 1)).filter(Boolean)
                    if (empty.length) errors.push(`Διάλεξε ομάδα για τα seeds ${empty.join(', ')}.`)
                    const teams = this.manualTeams.filter(Boolean)
                    if (new Set(teams).size !== teams.length) errors.push('Μια ομάδα είναι σε δύο seeds.')
                }
                return errors
            },
            payload() {
                return {
                    id: this.bracketId || 0,
                    seasonId: this.seasonId,
                    leagueId: this.leagueId,
                    cup: this.cup,
                    cupLeagueId: this.cupLeagueId,
                    title: this.title,
                    definition: {
                        seeding: this.cup ? 'manual' : this.seeding,
                        teams: this.seeding === 'manual' || this.cup ? this.manualTeams.map(teamId => teamId || 0) : [],
                        firstRound: this.pairs.map(pair => [pair.home || 0, pair.away || 0]),
                        rounds: this.roundsForm.map(round => ({ name: round.name, bestOf: round.bestOf, advantage: round.advantage }))
                    }
                }
            }
        },
        watch: {
            seasonId(value) {
                if (value) this.loadBracket()
            },
            payload: {
                deep: true,
                handler() {
                    this.schedulePreview()
                }
            }
        },
        created() {
            if (this.cup) this.seeding = 'manual'
            this.loadScope()
            this.loadSeasons()
            teamService.getFilteredTeams({ active: true }, 1, 1000).then(resp => {
                this.teams = (resp.data.content || []).slice().sort((a, b) => a.name.localeCompare(b.name))
            }).catch(() => {})
        },
        beforeDestroy() {
            clearTimeout(this.previewTimer)
        },
        methods: {
            seasonLabel,
            loadScope() {
                if (this.leagueId) {
                    leagueService.getLeague(this.leagueId).then(resp => {
                        this.scopeTitle = resp.data.title
                    }).catch(() => {})
                } else if (this.cupLeagueId) {
                    api.get(`cup-leagues/${this.cupLeagueId}`).then(resp => {
                        this.scopeTitle = resp.data.title
                    }).catch(() => {})
                }
            },
            loadSeasons() {
                Promise.all([
                    seasonService.getSeasons(1, 100).then(resp => sortSeasons(resp.data.content || [])),
                    configService.getConfig().then(resp => resp.data).catch(() => ({}))
                ]).then(([seasons, config]) => {
                    this.seasons = seasons
                    this.seasonId = (config && config.activeSeasonId) || (seasons[0] && seasons[0].id) || null
                }).catch(() => {
                    this.message = { type: 'error', text: 'Could not load the seasons.' }
                })
            },
            // the saved bracket of the season, or a new one from the standings
            loadBracket() {
                const scope = { seasonId: this.seasonId, leagueId: this.leagueId, cup: this.cup, cupLeagueId: this.cupLeagueId }
                const ranking = this.leagueId
                    ? leagueService.getRanking(this.leagueId, this.seasonId, 'regular_season').then(resp => resp.data || []).catch(() => [])
                    : Promise.resolve([])
                Promise.all([bracketService.getBracket(scope).then(resp => resp.data || null), ranking]).then(([bracket, rank]) => {
                    this.ranking = rank
                    if (bracket && bracket.definition) {
                        this.fill(bracket)
                    } else {
                        this.bracketId = null
                        this.title = ''
                        this.seeding = this.cup ? 'manual' : 'standings'
                        const count = this.cup ? 8 : Math.max(2, Math.min(8, rank.length || 8))
                        this.manualTeams = Array(count).fill(null)
                        this.roundsForm = []
                        this.changeTeamCount(count)
                    }
                }).catch(error => {
                    this.message = { type: 'error', text: (error.response && error.response.data && error.response.data.message) || 'Could not load the bracket.' }
                })
            },
            fill(bracket) {
                const definition = bracket.definition
                this.bracketId = bracket.id
                this.title = bracket.title || ''
                this.seeding = this.cup ? 'manual' : (definition.seeding || 'standings')
                this.pairs = (definition.firstRound || []).map(pair => ({ home: pair[0] || 0, away: pair[1] || 0 }))
                const teams = (definition.teams || []).map(teamId => teamId || null)
                const maxSeed = Math.max(2, ...this.pairs.flatMap(pair => [pair.home, pair.away]))
                this.teamCount = this.seeding === 'manual' ? Math.max(teams.length, maxSeed) : maxSeed
                this.manualTeams = Array.from({ length: this.teamCount }, (item, index) => teams[index] || null)
                this.roundsForm = (definition.rounds || []).map(round => ({ name: round.name, bestOf: round.bestOf || 1, advantage: !!round.advantage }))
            },
            seedName(seed) {
                if (this.seeding === 'manual' || this.cup) {
                    const team = this.teams.find(item => item.id === this.manualTeams[seed - 1])
                    return team ? team.name : ''
                }
                const rank = this.ranking.find(item => item.position === seed)
                return rank ? rank.name : ''
            },
            changeTeamCount(count) {
                this.teamCount = count
                this.manualTeams = Array.from({ length: count }, (item, index) => this.manualTeams[index] || null)
                this.resetPairs()
            },
            // the usual pairs (1-8, 4-5, 2-7, 3-6) with byes for the best seeds when the teams are not a power of two
            resetPairs() {
                const size = powerOfTwo(this.teamCount)
                const order = seedOrder(size)
                const pairs = []
                for (let index = 0; index < order.length; index += 2) {
                    const home = order[index] <= this.teamCount ? order[index] : 0
                    const away = order[index + 1] <= this.teamCount ? order[index + 1] : 0
                    pairs.push({ home, away })
                }
                this.pairs = pairs
                // the rounds keep their format, matched from the final backwards
                const count = Math.log2(size)
                const old = this.roundsForm
                this.roundsForm = Array.from({ length: count }, (item, index) => {
                    const remaining = count - index
                    const previous = old[old.length - remaining]
                    return previous ? { ...previous } : { name: roundName(remaining, index), bestOf: 1, advantage: false }
                })
            },
            setSeed(index, side, seed) {
                this.pairs.splice(index, 1, { ...this.pairs[index], [side]: seed })
            },
            setManualTeam(index, teamId) {
                this.manualTeams.splice(index, 1, teamId || null)
            },
            swapPair(index) {
                const pair = this.pairs[index]
                this.pairs.splice(index, 1, { home: pair.away, away: pair.home })
            },
            movePair(index, step) {
                const pairs = this.pairs.slice()
                const [pair] = pairs.splice(index, 1)
                pairs.splice(index + step, 0, pair)
                this.pairs = pairs
            },
            isWrongSeed(seed) {
                return seed > 0 && this.usedSeeds.filter(item => item === seed).length > 1
            },
            schedulePreview() {
                clearTimeout(this.previewTimer)
                if (!this.seasonId || !this.pairs.length || this.errors.length) return
                this.previewTimer = setTimeout(this.loadPreview, 400)
            },
            loadPreview() {
                const request = ++this.previewRequest
                this.previewLoading = true
                bracketService.previewBracket(this.payload).then(resp => {
                    if (request !== this.previewRequest) return
                    this.preview = resp.data
                    this.previewError = null
                }).catch(error => {
                    if (request !== this.previewRequest) return
                    this.previewError = (error.response && error.response.data && error.response.data.message) || 'Could not draw the bracket.'
                }).finally(() => {
                    if (request === this.previewRequest) this.previewLoading = false
                })
            },
            save() {
                this.saving = true
                bracketService.saveBracket(this.payload).then(resp => {
                    this.bracketId = resp.data.content
                    this.message = { type: 'success', text: 'Το δέντρο αποθηκεύτηκε και φαίνεται στο site.' }
                }).catch(error => {
                    this.message = { type: 'error', text: (error.response && error.response.data && error.response.data.message) || 'Could not save the bracket.' }
                }).finally(() => {
                    this.saving = false
                })
            },
            remove() {
                this.deleting = true
                bracketService.deleteBracket(this.bracketId).then(() => {
                    this.confirmDelete = false
                    this.message = { type: 'success', text: 'Το δέντρο διαγράφηκε.' }
                    this.loadBracket()
                }).catch(error => {
                    this.message = { type: 'error', text: (error.response && error.response.data && error.response.data.message) || 'Could not delete the bracket.' }
                }).finally(() => {
                    this.deleting = false
                })
            },
            // the board as a PNG: logos off, the storage does not allow drawing them in an image
            async downloadImage() {
                this.exporting = true
                try {
                    await this.$nextTick()
                    const node = this.$refs.board.$refs.board
                    const dataUrl = await toPng(node, { pixelRatio: 2, skipFonts: true, cacheBust: false })
                    const name = String(this.title || this.defaultTitle).trim().replace(/[\s/\\:*?"<>|.]+/g, '-')
                    const link = document.createElement('a')
                    link.href = dataUrl
                    link.download = `${name || 'bracket'}.png`
                    link.click()
                } catch (error) {
                    this.message = { type: 'error', text: 'Could not create the image.' }
                } finally {
                    this.exporting = false
                }
            }
        }
    }
</script>

<style scoped>
.bracket-page__intro {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.6);
}

.bracket-card {
    height: 100%;
    padding: 16px;
}

.bracket-card__title {
    display: flex;
    align-items: center;
    margin-bottom: 14px;
    font-size: 15px;
    font-weight: 700;
}

.bracket-card__subtitle {
    margin-bottom: 8px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: rgba(0, 0, 0, 0.55);
}

.seed-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
}

.seed-row--static {
    padding: 4px 8px;
    border-radius: 4px;
    background: #f7f7f8;
    font-size: 13px;
}

.seed-row__num {
    flex: 0 0 30px;
    font-weight: 700;
    font-size: 13px;
}

.seed-row__name {
    flex: 1 1 auto;
}

.seed-row__record {
    color: rgba(0, 0, 0, 0.5);
    font-variant-numeric: tabular-nums;
}

.pair-group {
    margin-bottom: 12px;
    padding: 8px 8px 6px 10px;
    border-left: 3px solid #ff7133;
    border-radius: 4px;
    background: #fafafa;
}

.pair-group--single {
    border-left-color: rgba(0, 0, 0, 0.2);
}

.pair-group__next {
    font-size: 11px;
    color: rgba(0, 0, 0, 0.55);
}

.pair-row {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 6px;
}

.pair-row__num {
    flex: 0 0 18px;
    font-size: 12px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.5);
}

.pair-row__vs {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.5);
}

.pair-row__tools {
    display: flex;
    flex: 0 0 auto;
}

.round-row {
    margin-bottom: 14px;
}

.round-row__format {
    display: flex;
    align-items: center;
    margin-top: 6px;
}

.bracket-preview__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
}
</style>
